import { ChangeEvent, memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContent } from 'src/contexts/ContentContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useListingAutoPricingSettings } from 'src/modals/ListingDetails/components/useListingAutoPricingSettings';
import {
  CheckIcon,
  CrossIcon,
  IconsFill,
  ProcessingIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import { Feature } from 'src/WebApiController';

import { useListingTableActionContextSubmitPricingSettingsChangeSelector } from '../../ListingTableActionContext/ListingTableActionContext.selectors';
import {
  ListingPricingInput,
  ListingPricingSettingsCellProps,
} from '../ListingPriceForm.types';
import { useCellDisabled, useIsSubmittingField } from '../useCellState';
import * as styles from './Cell.css';

const CircuitBreakerVelocityCellRaw: React.FC<
  ListingPricingSettingsCellProps
> = ({ listing, editable, event }) => {
  const hasReworkListingPriceFormFeature = useUserHasFeature(
    Feature.ReworkListingPriceForm
  );
  const formMethods = useFormContext<ListingPricingInput>();
  const { watch, formState, getFieldState, setValue } = formMethods;
  const onSubmitPricingSettingsChange =
    useListingTableActionContextSubmitPricingSettingsChangeSelector();

  const saveMsg = useContent(ContentId.Save);
  const cancelMsg = useContent(ContentId.Cancel);
  const rowIndex = watch('rowIndex');

  const {
    circuitBreakerMaxDiscountVelocityPercent,
    canAutoPrice,
    onCircuitBreakerChange,
  } = useListingAutoPricingSettings(listing);

  const isDisabled = useCellDisabled(
    listing,
    canAutoPrice,
    editable,
    event.virtId
  );

  const isSubmittingField = useIsSubmittingField(
    'circuitBreakerMaxDiscountVelocityPercent'
  );

  const onUpdateVelocity = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value);
      if (value <= 0 || value > 100 || isNaN(value)) {
        return;
      }
      onCircuitBreakerChange(undefined, undefined, value / 100);
    },
    [onCircuitBreakerChange]
  );

  return (
    <TableCellDiv align="right" showTooltip={false}>
      <PosFormField
        errors={
          getFieldState('circuitBreakerMaxDiscountVelocityPercent', formState)
            ?.error?.message
        }
        showErrorsInline
        className={styles.formFieldContainer}
      >
        <PosTextField
          className={styles.formTextFiled}
          value={roundToPrecision(
            (circuitBreakerMaxDiscountVelocityPercent ?? 10) * 100,
            2
          )}
          disabled={isDisabled}
          type="number"
          min={0}
          onChange={onUpdateVelocity}
          postfixDisplay={
            <>
              <div>%</div>
              {!isDisabled &&
              circuitBreakerMaxDiscountVelocityPercent !==
                formState.defaultValues
                  ?.circuitBreakerMaxDiscountVelocityPercent ? (
                <Stack direction="row" gap="s">
                  <CheckIcon
                    title={saveMsg}
                    withHoverEffect
                    size={vars.iconSize.s}
                    fill={IconsFill.textSuccess}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasReworkListingPriceFormFeature) {
                        onSubmitPricingSettingsChange({
                          listing,
                          formMethods,
                          fieldName: 'circuitBreakerMaxDiscountVelocityPercent',
                        });
                      } else {
                        setValue('isSubmittingPricingSettings', true);
                        setValue('isSubmittingRowIndex', rowIndex);
                        setValue(
                          'submittingField',
                          'circuitBreakerMaxDiscountVelocityPercent'
                        );
                      }
                    }}
                  />
                  <CrossIcon
                    title={cancelMsg}
                    withHoverEffect
                    size={vars.iconSize.s}
                    onClick={(e) => {
                      e.stopPropagation();
                      setValue(
                        'circuitBreakerMaxDiscountVelocityPercent',
                        formState.defaultValues
                          ?.circuitBreakerMaxDiscountVelocityPercent ?? null
                      );
                    }}
                  />
                </Stack>
              ) : isSubmittingField ? (
                <div className="operation-button">
                  <RotatingWrapper>
                    <ProcessingIcon size={vars.iconSize.s} />
                  </RotatingWrapper>
                </div>
              ) : undefined}
            </>
          }
        />
      </PosFormField>
    </TableCellDiv>
  );
};

export const CircuitBreakerVelocityCell = memo(CircuitBreakerVelocityCellRaw);
